import React, { useState, useMemo, useEffect } from 'react';
import { Modal, Alert, Button, Form, Radio, DatePicker,
    Space, InputNumber, Input, Checkbox, 
    Table } from 'antd';
import { CompassOutlined, DeleteOutlined, EditOutlined, EyeOutlined, MailOutlined, PlusOutlined, QuestionOutlined, ReloadOutlined, StopOutlined, TranslationOutlined, UserOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import FormTimeZone from '../../pages/GenericCreateEdit/TimezoneSelect';
import ILTAddressesDropDown from '../ILTAddresses/ILTAddressesDropDown';
import ILTInstructorDropDown from '../ILTInstructors/ILTInstructorDropDown';
import ILTCodesDropDown from './ILTCodesDropDown';
import useCreateSession from '../../gql/hooks/useCreateSession';
import useUpdateSession from '../../gql/hooks/useUpdateSession';
import { DATE_TIME_FORMAT as DT_FORMAT, formatDTPayload, getErrorMessages, isUnchangedDT, isValidURL, nowInZone, parseDTPayload, syncTimeFieldsToTimezone } from '../../utils';
import ExpandableAlert from '../../../components/errors/ExpandableAlert';
import { LanguageSelectorCMX } from '../../../components/formItems';
import { DEFAULT_LOCALE_CODE, DEFAULT_LOCALE_NAME } from '../../../const';
import { useLazyQuery, useMutation } from '@apollo/client';
import { ACTIVATE_SESSION, CANCEL_SESSION } from '../../gql/mutations/SET_SESSION_STATUS';
import classNames from 'classnames';
import DELETE_SESSION from '../../gql/mutations/DELETE_SESSION';
import ILT_SESSION_QUERY from '../../gql/queries/ILT_SESSION_QUERY';
import { Tooltip } from 'antd';

dayjs.extend( utc );
dayjs.extend( timezone );
dayjs.extend( isSameOrAfter );
dayjs.extend( isSameOrBefore );

const initialValues = {
    addressID: '',
    maxSeats: 0,
    name: '',
    completionCodeIDs: [],
    meetingUrl: '',
    registrationUrl: '',
    instructorIDs: [],
    type: 'virtual',
    timezone: dayjs.tz.guess()
};

// forms a PatchSessionInput from the original session dataobject. does not include any updated info
function getSessionPatchFromOriginal( session ) {
    const data = session._dataObject;
    return {
        sessionID: data.id,
        name: data.locales.find( l => l.locale == DEFAULT_LOCALE_CODE )?.name || '',
        locales: data.locales.map( ( { locale, name } ) => ( { locale, name } ) ),
        maxSeats: data.maxSeats,
        addressID: data.address?.id,
        instructorIDs: data.instructors.map( i => i.id ),
        completionCodeIDs: data.completionCodes.map( c => c.id ),
        sessionStartAt: data.sessionStartAt,
        sessionEndAt: data.sessionEndAt,
        registrationOpenAt: data.registrationOpenAt,
        registrationCloseAt: data.registrationCloseAt,
        timezone: data.timezone,
        meetingUrl: data.meetingUrl,
        registrationUrl: data.registrationUrl,
    };
}

const useIltSessionStatus = ( session ) => {

    const [ isStarted, setIsStarted ] = useState( false );
    const [ isEnded, setIsEnded ] = useState( false );
    
    const isRegistered = useMemo( () => {
        if ( !session ) {return false;}
        return session.filledSeats > 0;
    }, [ session ] );

    const isCompleted = useMemo( () => {
        if ( !session ) {return false;}
        return session.completionCount > 0;
    }, [ session ] );
    
    useEffect( () => {
        if ( !session ) {
            setIsStarted( false );
            setIsEnded( false );
            return;
        }

        const check = () => {
            const now = dayjs();
            setIsStarted( dayjs( session.sessionStartAt )?.isBefore( now ) );
            setIsEnded( dayjs( session.sessionEndAt )?.isBefore( now ) );
        };

        check();

        const intId = setInterval( check, 1000 * 30 );

        return () => {
            clearInterval( intId );
        };
    }, [ session ] );

    return { isRegistered, isCompleted, isStarted, isEnded };
};

const FakeLocaleInput = ( { value, onChange, disabled } ) => {
    return (
        <Space.Compact className='localization'>
            <Input 
                value={ value } 
                disabled={ disabled }
                onChange={ e => onChange( e.target.value ) } />
            <Tooltip title={ `Only ${ DEFAULT_LOCALE_NAME } can be modified on this action` }>
                <Button disabled>{ DEFAULT_LOCALE_CODE }</Button>
            </Tooltip>
        </Space.Compact>
    );
};

export const CreateEditILTSession = ( { finishedCallback, iltID, editSession } ) => {
    const [ form ] = Form.useForm(); 
    const [ isSaving, setIsSaving ] = useState( false );
    const { createSession } = useCreateSession();
    const { updateSession } = useUpdateSession();
    const [ showRegURLHelp, setShowRegURLHelp ] = useState( false );
    const [ showRegistration, setShowRegistration ] = useState( false );

    const [ error, setError ] = useState();
    const [ open, setOpen ] = useState( false );

    const [ selectedTimezone, setSelectedTimezone ] = useState();
    const { isRegistered, isStarted, isEnded } = useIltSessionStatus( editSession );

    const disabled = useMemo( () => isEnded || isSaving, [ isEnded, isSaving ] );

    function close() {
        setOpen( false );
        form.resetFields();
    }

    /**
     * The pickers show wall clocks in the selected zone, so switching zones re-reads them
     * in the new one — same instant, different displayed time.
     *
     * @param {string} tz   IANA zone just picked in the Time Zone select. `prevTz` is the
     *                      zone the pickers' current wall clocks mean, so it is read off
     *                      state before that state is replaced.
     * @returns {void} the datetime fields are updated on the form
     */
    function onTimezoneChange( tz ) {
        const prevTz = selectedTimezone;
        setSelectedTimezone( tz );

        if ( !prevTz || prevTz === tz ) {
            return;
        }

        syncTimeFieldsToTimezone( prevTz, tz, [ 'sessionStartAt', 'sessionEndAt', 'registrationOpenAt', 'registrationCloseAt' ], form );
    }

    const hasCustomRegistrationWindow = !!( editSession?.registrationOpenAt || editSession?.registrationCloseAt );

    const formEditValues = useMemo( () => {
        if ( !editSession ) {
            return { ...initialValues };
        }

        // The stored times are UTC instants; show them as wall clocks in the session's zone.
        const tz = editSession.timezone;

        return {
            ...getSessionPatchFromOriginal( editSession ),
            ...editSession,
            addressID: editSession.address?.id,
            instructorIDs: editSession.instructors.map( i => i.id ),
            type: editSession.meetingUrl ? 'virtual' : 'live',
            registrationCloseAt: hasCustomRegistrationWindow ? parseDTPayload( editSession.registrationCloseAt, tz ) : null,
            registrationOpenAt: hasCustomRegistrationWindow ? parseDTPayload( editSession.registrationOpenAt, tz ) : null,
            sessionStartAt: parseDTPayload( editSession.sessionStartAt, tz ),
            sessionEndAt: parseDTPayload( editSession.sessionEndAt, tz ),
        };
    }, [ editSession, hasCustomRegistrationWindow ] );

    useEffect( () => {
        setShowRegistration( hasCustomRegistrationWindow );
    }, [ hasCustomRegistrationWindow ] );

    // Keep the timezone the date pickers are bound to in step with the session being edited.
    useEffect( () => {
        setSelectedTimezone( editSession?.timezone ?? dayjs.tz.guess() );
    }, [ editSession ] );

    useEffect( () => {
        if ( formEditValues ) {
            form.setFieldsValue( formEditValues );
        }
    }, [ formEditValues ] );

    const handleSubmit = async ( values ) => {
        setIsSaving( true );
        setError( );

        const payload = {
            iltID,
            addressID: values.type === 'live' ? values.addressID : '',
            instructorIDs: values.instructorIDs,
            name: values.name,
            locales: [ {
                locale: DEFAULT_LOCALE_CODE,
                name: values.name
            }, ...( editSession?.locales ?? [] ).map( ( { locale, name } ) => ( { locale, name } ) ).filter( l => l.locale != DEFAULT_LOCALE_CODE ) ],
            completionCodeIDs: values.completionCodeIDs,
            registrationUrl: values.registrationUrl,
            meetingUrl: values.type === 'live' ? '' : values.meetingUrl || '',
            maxSeats: values.maxSeats,
            timezone: values.timezone,
            // The picked times are wall clocks in values.timezone; store them as UTC instants.
            registrationCloseAt: formatDTPayload( values.registrationCloseAt, values.timezone ),
            registrationOpenAt: formatDTPayload( values.registrationOpenAt, values.timezone ),
            sessionStartAt: formatDTPayload( values.sessionStartAt, values.timezone ),
            sessionEndAt: formatDTPayload( values.sessionEndAt, values.timezone ),
        };

        if ( !editSession ) {
            try {
                
                const response = await createSession( payload );

                if ( response.success ) {
                    finishedCallback( response.response );
                    setOpen( false );
                    form.resetFields();
                } else {
                    setError( <ExpandableAlert type='error' message='Could not create session' description={ getErrorMessages( response ) } /> );
                }
            } catch ( e ) {
                console.error( e );
                setError( <ExpandableAlert type='error' message='Could not create session' description={ e.toString() } /> );
            } 
        } else {
            payload.sessionID = editSession.id;

            try {
                const response = await updateSession( payload );

                if ( response.success ) {
                    finishedCallback( response.response );
                    close();
                } else {
                    setError( <ExpandableAlert type='error' message='Could not save session' description={ getErrorMessages( response ) } /> );
                }
            } catch ( e ) {
                console.error( e );
                setError( <ExpandableAlert type='error' message='Could not save session' description={ e.toString() } /> );
            } 
        }

        setIsSaving( false );
    };

    const handleSave = () => {
        form.submit();
    };
    
    const handleCancel = () => {
        setError();
        close();
    };
    
    const handleHelpClick = () => {
        setShowRegURLHelp( !showRegURLHelp );
    };
    
    const handleToggleRegistraion = () => {
        const newCheck = !showRegistration;
        if ( !newCheck ) {
            form.resetFields( [ 'registrationOpenAt', 'registrationCloseAt' ] );
        }
        setShowRegistration( newCheck );
    };

    function handleOpen() {
        setOpen( true );
    }
    
    return (
        <>
            { !editSession ?
                <Button
                    primary
                    icon={ <PlusOutlined /> }
                    onClick={ handleOpen }
                    >Add</Button> :
                disabled ?
                    <span 
                        className={ classNames( 'dropdown-button' ) }
                        onClick={ handleOpen }
                        ><EyeOutlined /> View</span> :    
                    <span 
                        className={ classNames( 'dropdown-button' ) }
                        onClick={ handleOpen }
                        ><EditOutlined /> Edit</span>
            }
            <Modal
                open={ open }
                title={ editSession ? 'Edit Session' : 'Add Session' }
                onOk={ handleSave } 
                onCancel={ handleCancel } 
                confirmLoading={ isSaving }
                okButtonProps={ { disabled } }
                maskClosable={ false }
                okText="Save"
                width={ 800 }
                wrapClassName="modal-session"
                bodyStyle={ { overflowY: 'auto', maxHeight: 'calc(100vh - 200px)' } } 
 
            >
                <Form
                    form={ form }
                    disabled={ disabled }
                    name="session"
                    layout="vertical"
                    initialValues={ formEditValues }
                    onFinish={ handleSubmit }
                    scrollToFirstError
                >   
                    { error && error }

                    <div className="main-columns">
                        <div className="left">

                            <Form.Item
                                label="Name"
                                name="name"
                                rules={ [
                                    {
                                        required: true,
                                        message: 'Session Name is required',
                                    },
                                    {
                                        max: 100,
                                        type: 'string',
                                        message: 'Names must be 100 characters or shorter.',
                                    }
                                ] }
                            >

                                <FakeLocaleInput disabled={ disabled || isSaving } />
                            </Form.Item>

                            <Space className='small-items'>

                                <Form.Item 
                                    label='Session Type'
                                    name="type"
                                >
                                    <Radio.Group  
                                        disabled={ isStarted || isRegistered || disabled }
                                        buttonStyle="solid"   
                                        style={ { width: 'max-content' } }
                                    >
                                        <Radio.Button value={ 'live' }>
                                            Live
                                        </Radio.Button>
                                        <Radio.Button value={ 'virtual' }>
                                            Virtual
                                        </Radio.Button>
                                    </Radio.Group>
                                </Form.Item>
                                <Form.Item
                                    label="Seats"
                                    name="maxSeats"
                                    rules={ [
                                        {
                                            validator: ( _, value ) => {

                                                if ( editSession?.filledSeats > value ) {
                                                    return Promise.reject( new Error( 'Can not have fewer seats than current number of registrants' ) );
                                                }
                                                return Promise.resolve();
                                            }
                                        },
                                        {
                                            validator: ( _, value ) => {
                                                if ( value && value > 0 ) {
                                                    return Promise.resolve();
                                                } else {
                                                    return Promise.reject( new Error( 'Seats must be greater than zero' ) );
                                                }
                                            }
                                        },
                                        {
                                            validator: ( _, value ) => {
                                                if ( value < 10001  ) {
                                                    return Promise.resolve();
                                                } else {
                                                    return Promise.reject( new Error( 'Max seats is 10,000' ) );
                                                }
                                            }
                                        },
                                    ] }
                                >
                                    <InputNumber 
                                        min={ editSession?.filledSeats ?? 0 }
                                        disabled={ isStarted || disabled }
                                        step={ 1 }
                                    />
                                </Form.Item>

                            </Space>
                    
 
                            { /* no need to disable bc this is only disabled after session end, wherein the modal is not accessible */ }
                            <ILTInstructorDropDown/>
                    
                            <Form.Item
                                noStyle
                                shouldUpdate={ ( prevValues, currentValues ) => 
                                    ( prevValues.type !== currentValues.type ) }
                            >
                                { ( { getFieldValue } ) => {
                                    if ( getFieldValue( 'type' ) === 'live' ) {
                                        return <ILTAddressesDropDown disabled={ isStarted || disabled }/>;
                                    }
                                } }
                            </Form.Item>

                            <ILTCodesDropDown iltID={ iltID } disabled={ isStarted || disabled } />
 
                            <Form.Item
                                noStyle
                                shouldUpdate={ ( prevValues, currentValues ) => 
                                    ( prevValues.type !== currentValues.type ) }
                            >
                                { ( { getFieldValue } ) => {
                                    if ( getFieldValue( 'type' ) === 'virtual' ) {
                                        return (
                                            <Form.Item  
                                                label="Meeting URL"
                                                name="meetingUrl"
                                                rules={ [ {
                                                    required: true,
                                                    validator: ( _, v ) => {
                                                        if ( !( v?.length > 0 ) ) {
                                                            return Promise.reject( new Error( 'Meeting URL is required' ) );
                                                        }
                                                        if ( !v.match( /^http(s?):\/\// ) ) {
                                                            return Promise.reject( new Error( 'Must include protocol ("http://" or "https://")' ) );
                                                        }
                                                        return isValidURL( v ) ? Promise.resolve() : Promise.reject( new Error( 'Invalid URL' ) );
                                                    }
                                                } ] }
                                            >
                                                { /* no need to disable bc this is only disabled after session end, wherein the modal is not accessible */ }
                                                <Input autoComplete='off' style={ { width: '300px' } }/>
                                            </Form.Item>
                                        );
                                    }
                                } }
                            </Form.Item>

                            <Space>
                                <Form.Item  
                                    label="Registration URL"
                                    name="registrationUrl"
                                    style={ { width: 300 } }
                                    rules={ [ {
                                        validator: ( _, v ) => {
                                            if ( !( v?.length > 0 ) ) {
                                                return Promise.resolve();
                                            }
                                            if ( !v.match( /^http(s?):\/\// ) ) {
                                                return Promise.reject( new Error( 'Must include protocol ("http://" or "https://")' ) );
                                            }
                                            return isValidURL( v ) ? Promise.resolve() : Promise.reject( new Error( 'Invalid URL' ) );
                                        }
                                    } ] }
                                >
                                    <Input disabled={ isStarted || disabled } style={ { width: '300px' } }/>
                                </Form.Item> 

                                <Button
                                    size="small" 
                                    shape="circle"
                                    disabled={ false }
                                    style={ { marginTop: '4px' } }
                                    onClick={ handleHelpClick }
                                >
                                    <QuestionOutlined />
                                </Button>
                            </Space>

                            { showRegURLHelp &&
                                <Alert  
                                    style={ { marginBottom: '24px' } }
                                    type="warning"
                                    message="Registration URL"
                                    description="Registration URLs should only be provided for Live sessions that require 3rd party registration to be completed."
                                />
                            }
                        </div>

                        <div className="right">

                            
                            <FormTimeZone form={ form } onChange={ onTimezoneChange } /> 
                            <Alert message={ <p>{ `Dates and times will be in UTC${ dayjs().tz( selectedTimezone ).format( 'Z' ) }` }</p> } description={ <p><CompassOutlined />{ `Your current timezone is UTC${ dayjs().tz( dayjs.tz.guess() ).format( 'Z' ) }` }</p> } />
                            
                    
                            <Checkbox 
                                style={ { marginBottom: '12px' } }
                                onChange={ handleToggleRegistraion }
                                checked={ showRegistration }
                            >
                                Add Custom Registration Timeframe
                            </Checkbox>

                            { showRegistration && 
                                <>
                                    <Form.Item  
                                        label="Registration Open"
                                        name="registrationOpenAt"
                                        dependencies={ [ 'timezone', 'sessionStartAt' ] }
                                        rules={ [
                                            ( { getFieldValue } ) => ( {
                                                validator: ( _, value ) => {
                                                    const tz = getFieldValue( 'timezone' );

                                                    if ( !value || isUnchangedDT( editSession?.registrationOpenAt, value, tz ) ) {
                                                        return Promise.resolve();
                                                    }

                                                    const start = getFieldValue( 'sessionStartAt' );
                                                    const reg = dayjs( value );
                                                    const now = nowInZone( tz );

                                                    if ( reg.isBefore( now ) ) {
                                                        return Promise.reject( new Error( 'Registration Open must be in the future' ) );
                                                    } else if ( start && reg.isSameOrAfter( start ) ) {
                                                        return Promise.reject( new Error( 'Registration Open must be before Session Start' ) );
                                                    } else {
                                                        return Promise.resolve();
                                                    }
                                                }
                                            } )
                                        ] }
                                    >
                                        <DatePicker 
                                            inputReadOnly
                                            allowClear
                                            disabled={ isRegistered || isStarted || disabled }
                                            format={ DT_FORMAT }
                                            showNow={ false }
                                            showTime={ {
                                                minuteStep: 5  
                                            } }
                                            disabledDate={ ( curr ) => curr.isBefore( nowInZone( selectedTimezone ).startOf( 'day' ) ) }
                                        />
                                    </Form.Item> 
                    
                                    <Form.Item  
                                        label="Registration Close"
                                        name="registrationCloseAt"
                                        dependencies={ [ 'timezone', 'sessionStartAt', 'registrationOpenAt' ] }
                                        rules={ [
                                            ( { getFieldValue } ) => ( {
                                                validator: ( _, value ) => {
                                                    const tz = getFieldValue( 'timezone' );

                                                    if ( !value || isUnchangedDT( editSession?.registrationCloseAt, value, tz ) ) {
                                                        return Promise.resolve();
                                                    }

                                                    const start = getFieldValue( 'sessionStartAt' );
                                                    const regStart = getFieldValue( 'registrationOpenAt' );
                                                    const regClose = dayjs( value );
                                                    const now = nowInZone( tz );

                                                    if ( regClose.isBefore( now ) ) {
                                                        return Promise.reject( new Error( 'Registration Close must be in the future' ) );
                                                    } else if ( regStart && regClose.isSameOrBefore( regStart ) ) {
                                                        return Promise.reject( new Error( 'Registration Close must be after Registration Open' ) );
                                                    } else if ( start && regClose.isSameOrAfter( start ) ) {
                                                        return Promise.reject( new Error( 'Registration Close must be before Session Start' ) );
                                                    } else {
                                                        return Promise.resolve();
                                                    }
                                                }
                                            } )
                                        ] }
                                    >
                                        <DatePicker 
                                            allowClear
                                            inputReadOnly
                                            disabled={ isRegistered || isStarted || disabled }
                                            format={ DT_FORMAT }
                                            showNow={ false }
                                            showTime={ {
                                                minuteStep: 5 
                                            } }
                                            disabledDate={ ( curr ) => curr.isBefore( nowInZone( selectedTimezone ).startOf( 'day' ) ) }
                                        />
                                    </Form.Item> 
                                </>
                            }
                            <Form.Item  
                                label="Session Start At"
                                name="sessionStartAt"
                                dependencies={ [ 'timezone' ] }
                                rules={ [
                                    {
                                        required: true,
                                        message: 'Session Start is required',
                                    },
                                    ( { getFieldValue } ) => ( {
                                        validator: ( _, value ) => {
                                            const tz = getFieldValue( 'timezone' );

                                            if ( !value || isUnchangedDT( editSession?.sessionStartAt, value, tz ) ) {
                                                return Promise.resolve();
                                            }

                                            const sessionStart = dayjs( value );
                                            const now = nowInZone( tz );

                                            if ( sessionStart.isBefore( now ) ) {
                                                return Promise.reject( new Error( 'Session Start must be in the future' ) );
                                            } else {
                                                return Promise.resolve();
                                            }
                                        }
                                    } )
                                ] }
                            >
                                <DatePicker 
                                    inputReadOnly
                                    allowClear
                                    disabled={ isRegistered || isStarted || disabled }
                                    format={ DT_FORMAT }
                                    showNow={ false }
                                    showTime={ {
                                        minuteStep: 5  
                                    } }
                                    disabledDate={ curr => curr.isBefore( nowInZone( selectedTimezone ).startOf( 'day' ) ) }
                                />
                            </Form.Item> 
                            <Form.Item  
                                label="Session End At"
                                name="sessionEndAt"
                                dependencies={ [ 'timezone', 'sessionStartAt' ] }
                                rules={ [
                                    {
                                        required: true,
                                        message: 'Session End is required',
                                    },
                                    ( { getFieldValue } ) => ( {
                                        validator: ( _, value ) => {
                                            const tz = getFieldValue( 'timezone' );

                                            if ( !value || isUnchangedDT( editSession?.sessionEndAt, value, tz ) ) {
                                                return Promise.resolve();
                                            }

                                            const sessionStart = getFieldValue( 'sessionStartAt' );
                                            const sessionEnd = dayjs( value );
                                            const now = nowInZone( tz );

                                            if ( sessionEnd.isBefore( now ) ) {
                                                return Promise.reject( new Error( 'Session End must be in the future' ) );
                                            } else if ( sessionStart && sessionStart.isSameOrAfter( sessionEnd ) ) {
                                                return Promise.reject( new Error( 'Session End must be after Session Start' ) );
                                            } else {
                                                return Promise.resolve();
                                            }
                                        }
                                    } )
                                ] }
                            > 
                                <DatePicker 
                                    allowClear
                                    inputReadOnly
                                    disabled={ isStarted || isRegistered || disabled }
                                    format={ DT_FORMAT }
                                    showNow={ false }
                                    showTime={ {
                                        minuteStep: 5  
                                    } }
                                    disabledDate={ ( curr ) => curr.isBefore( nowInZone( selectedTimezone ).startOf( 'day' ) ) }
                                /> 
                            </Form.Item> 
                        </div>
                    </div>
                    { isEnded ? <ExpandableAlert message={ 'This session has ended and is no longer editable' } /> :
                        isStarted && isRegistered ? <ExpandableAlert message={ `This session has begun and ${ editSession.filledSeats } users have already registered` } description={ 'Some fields are no longer editable' } /> :
                        isStarted ? <ExpandableAlert message={ 'This session has begun, so some fields are no longer editable' } /> :
                        isRegistered ? <ExpandableAlert message={ `${ editSession.filledSeats } users have already registered for this session` } description={ 'Some fields can not be edited now that users have registered.' } /> : <></> }

                </Form>
            </Modal>
        </>
    );
};

export const EditILTSessionLocalizations = ( { iltID, session, finishedCallback } ) => {
    
    const { updateSession } = useUpdateSession();
    const { isEnded } = useIltSessionStatus( session );

    const [ open, setOpen ] = useState( false );
    const [ saving, setSaving ] = useState( false );
    const [ error, setError ] = useState( null );
    const disabled = useMemo( () => isEnded, [ isEnded ] );
    
    const [ maxKey, setMaxKey ] = useState( 0 );
    const [ displayData, setDisplayData ] = useState( [] );

    const excludedLocales = useMemo( () => {
        return displayData.map( t => t.locale ).filter( t => !!t );
    }, [ displayData ] );

    function toTableData( locales ) {
        const sorted = [ ...locales ];
        sorted.sort( ( a, b ) => a.locale.localeCompare( b.locale ) );
        const defaultIndex = sorted.findIndex( ( { locale } ) => locale == DEFAULT_LOCALE_CODE );
        const defaultLoc = defaultIndex >= 0 ? sorted.splice( defaultIndex, 1 ) : [];
        return [ ...defaultLoc, ...sorted ].map( ( t, i ) => ( { key: i, locale: t.locale, name: t.name, permanent: true } ) );
    }

    useEffect( () => {
        setMaxKey( session.locales.length );
        setDisplayData( toTableData( session.locales ) );
    }, [ session ] );

    async function saveLocalizations( locales ) {
        setSaving( true );

        const payload = {
            ...getSessionPatchFromOriginal( session ),
            iltID,
            name: locales.find( l => l.locale == DEFAULT_LOCALE_CODE ).name,
            locales,
        };

        await updateSession( payload ).then( data => {
            if ( !data.success ) {
                console.error( data );
                setError( <ExpandableAlert message={ 'Could not save localizations' } description={ data.response.message } /> );
                return;
            } else {
                setError();
                setOpen( false );
                setDisplayData( toTableData( data.response.locales ) );
                finishedCallback?.();
            }

        } ).catch( e => {
            console.error( e );
            setError( <ExpandableAlert message={ 'Could not save localizations' } description={ e.message } /> );
        } ).finally( () => {
            setSaving( false );
        } );
    }

    function handleOkClick() {
        setError();
        if ( disabled ) {
            return;
        }
        const locales = displayData.filter( d => d.name?.trim().length > 0 && d.locale ).map( ( { name, locale } ) => ( { name, locale } ) );
        if ( locales.length == 0 || !locales.some( l => l.locale == DEFAULT_LOCALE_CODE ) ) {
            setError( <ExpandableAlert message={ 'Must include en-US localization' } /> );
            return;
        }
        saveLocalizations( locales );
    }

    function handleCancelClick() {
        setDisplayData( session.locales );
        setError();
        setOpen( false );
    }

    function onAddLocale() {
        setMaxKey( maxKey + 1 );
        setDisplayData( [ ...displayData, { key: maxKey + 1, locale: null, name: '' } ] );
    }

    function onDeleteLocale( key ) {
        if ( displayData.find( d => d.key == key )?.permanent ) {
            return;
        }
        setDisplayData( displayData.filter( d => d.key != key ) );
    }

    function onUpdateLocale( { key, locale, name } ) {
        setDisplayData( displayData.map( t => t.key == key ? ( { ...t, locale, name } ) : t ) );
    }

    function handleOpen() {
        setDisplayData( toTableData( session.locales ) );
        setOpen( true );
    }

    return <>
        <span className={ classNames( 'clickable dropdown-button' ) } onClick={ handleOpen }>
            <TranslationOutlined /> 
            Localizations
        </span>
        <Modal
            open={ open }
            okText='Update'
            onOk={ handleOkClick }
            confirmLoading={ saving }
            okButtonProps={ { disabled: disabled } }
            onCancel={ handleCancelClick }
            className='localization-modal'
            title='Localizations'
            >
                { displayData.map( ( locale ) => 
                    <Space.Compact key={ locale.key } className='localization'>
                        <LanguageSelectorCMX 
                            value={ locale?.locale ?? undefined } 
                            onChange={ loc => onUpdateLocale( { key: locale.key, locale: loc, name: locale.name } ) } 
                            mode="select-single" 
                            disabled={ locale.permanent || disabled || saving }
                            excludelocales={ locale.locale ? excludedLocales.filter( l => l != locale.locale ) : excludedLocales } 
                            hideExcludedLocales={ false } />
                        <Input 
                            defaultValue={ locale?.name ?? '' } 
                            disabled={ disabled || saving }
                            onChange={ e => onUpdateLocale( { key: locale.key, locale: locale.locale, name: e.target.value } ) } />
                        <Button onClick={ () => onDeleteLocale( locale.key ) } disabled={ displayData.length <= 1 || locale.permanent || disabled || saving }>
                            <DeleteOutlined />
                        </Button>
                    </Space.Compact> 
                ) }
                { !disabled && <Button icon={ <PlusOutlined /> } disabled={ saving } className='add-localization' onClick={ onAddLocale }>New</Button> }
                { disabled ? <ExpandableAlert message='The session has ended, so localizations can not be changed' /> : <></> }
                { error ? error : <></> }
        </Modal>
    </>;
};

export const CancelSession = ( { iltID, session, finishedCallback } ) => {

    const { isCompleted, isRegistered } = useIltSessionStatus( session );

    const [ isCanceled, setIsCanceled ] = useState( session.status != 'ACTIVE' );
    const disabled = useMemo( () => isCompleted, [ isCompleted ] );

    const [ open, setOpen ] = useState( false );
    const [ errorMessage, setErrorMessage ] = useState( null );

    const [ cancelSession ] = useMutation( CANCEL_SESSION );
    const [ activateSession ] = useMutation( ACTIVATE_SESSION );

    async function setSessionStatus( cancel ) {
        if ( disabled ) {
            return;
        }

        try {

            const payload = {
                iltID,
                sessionID: session.id,

            };

            if ( cancel ) {
                await cancelSession( { variables: payload } );
                setIsCanceled( true );
            } else {
                await activateSession( { variables: payload } );
                setIsCanceled( false );
            }
            finishedCallback?.();
            setErrorMessage();
            setOpen( false );
        } catch ( e ) {
            console.error( e );
            setErrorMessage( <ExpandableAlert message={ `Could not ${ cancel ? 'cancel' : 'activate' } session` } type='error' /> );
        } 
    }

    function handleClick() {
        if ( disabled ) {
            return;
        }
        setOpen( true );
    }

    return <>
        <span className={ classNames( 'clickable dropdown-button', { disabled } ) } onClick={ handleClick }>
            { isCanceled ? <ReloadOutlined /> : <StopOutlined /> }
            { isCanceled ? 'Activate' : 'Cancel' }
        </span>
        <Modal
            open={ open }
            okText='Confirm'
            onCancel={ () => setOpen( false ) }
            onOk={ () => setSessionStatus( !isCanceled ) }
            title={ `${ isCanceled ? 'Activate' : 'Cancel' } Session` }
            >
                <span>{ `Are you sure you want to ${ isCanceled ? 'activate' : 'cancel' } this session?` }</span>
                { isRegistered && !isCanceled ? <><br /><span>{ `${ session.filledSeats } users have already registered for this session` }</span><br /></> : <></> }
                { errorMessage ? errorMessage : <></> }
        </Modal>
    </>;
};

export const SessionRegistrantsModal = ( { iltID, session } ) => {

    const { isRegistered } = useIltSessionStatus( session );

    const [ open, setOpen ] = useState( false );
    const [ loading, setLoading ] = useState( false );
    const [ errorMessage, setErrorMessage ] = useState( null );
    const [ data, setData ] = useState( null );

    const [ fetchSession ] = useLazyQuery( ILT_SESSION_QUERY ); 

    async function fetchRegistrants() {
        setLoading( true );

        await fetchSession( {
            variables: {
                iltID,
                sessionID: session.id
            }
        } ).then( data => {
            const registrants = data?.data?.session?.node?.sessionRegistrations;

            setData( registrants );
            setErrorMessage( null );
        } ).catch( e => {
            console.log( e );
            setErrorMessage( <ExpandableAlert message='Could not fetch registrants' type='error' /> );
        } ).finally( () => {
            setLoading( false );
        } );
    }

    function handleClick() {
        if ( isRegistered ) {
            fetchRegistrants();
        }
        setOpen( true );
    }

    const columns = [
        {
            title: 'Name',
            dataIndex: 'person',
            key: 'name',
            render: ( person ) => <span>{ `${ person.surname }, ${ person.givenName }` }</span>
        }, {
            title: 'Email',
            dataIndex: [ 'person', 'email' ],
            key: 'email',
            render: ( email ) => <a href={ `mailto:${ email }` }>{ email }</a>
        }, {
            title: 'Registration Date',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: ( createdAt ) => dayjs( createdAt ).format( DT_FORMAT )
        }
    ];

    function onEmailAll( e ) {
        const mailto = `mailto:${ data.map( r => r.person.email ).join( ';' ) }`;
        window.location.href = mailto;
        e.preventDefault();
    }

    return <>
        <span className={ classNames( 'clickable dropdown-button' ) } onClick={ handleClick }>
            <UserOutlined /> 
            Registrants
        </span>
        <Modal
            open={ open }
            footer={ null }
            width={ 750 }
            onCancel={ () => setOpen( false ) }
            className='registrants-modal'
            title='Registrants'
            >
                { !isRegistered ?
                    <span>No users have registered for this session</span> :
                    <>
                        <Table
                            dataSource={ data }
                            columns={ columns }
                            loading={ loading }
                            pagination={ { 
                                hideOnSinglePage: true
                            } }
                        />
                        <Button disabled={ loading } icon={ <MailOutlined /> } onClick={ onEmailAll }>Email all users</Button>
                    </>
                }
                { errorMessage ? errorMessage : <></> }
        </Modal>
    </>;
};

export const DeleteSessionModal = ( { iltID, session, finishedCallback } ) => {

    const [ deleteSession ] = useMutation( DELETE_SESSION );

    const { isRegistered, isStarted } = useIltSessionStatus( session );

    const [ open, setOpen ] = useState( false );
    const [ loading, setLoading ] = useState( false );
    const [ errorMessage, setErrorMessage ] = useState( null );

    const disabled = useMemo( () => isRegistered || isStarted, [ isRegistered, isStarted ] );

    async function sendDeleteSession() {
        setLoading( true );
        try {
            await deleteSession( { variables: { iltID, sessionID: session.id } } );
            setErrorMessage( null );
            setOpen( false );
            finishedCallback?.();
        } catch ( e ) {
            console.error( e );
            setErrorMessage( <ExpandableAlert message='Could not delete session' type='error' /> );
        } finally {
            setLoading( false );
        }
    }

    function handleClick() {
        if ( disabled ) {
            return;
        }
        setOpen( true );
    }

    return <>
        <span className={ classNames( 'clickable dropdown-button', { disabled } ) } onClick={ handleClick }>
            <DeleteOutlined /> 
            Delete
        </span>
        <Modal
            open={ open }
            okText='Delete'
            okButtonProps={ { danger: true } }
            onOk={ () => sendDeleteSession() }
            confirmLoading={ loading }
            onCancel={ () => setOpen( false ) }
            title='Delete Session'
            >
                <span>{ 'Are you sure you want to delete this session?' }</span>
                <br />
                <span>{ 'This action can not be undone' }</span>
                { errorMessage ? errorMessage : <></> }
        </Modal>
    </>;
};